import { Instance, SnapshotIn, types, flow, getEnv, getSnapshot, applySnapshot } from 'mobx-state-tree';

import { performGetGradings } from 'NeoAnalytics/services';
import { IStoresEnv } from '@core/storesEnv';
import { addReview, postGradings } from 'NeoAnalytics/services/postGradings';

const asString = (value: unknown, fallback = '') => (typeof value === 'string' ? value : fallback);
const asNullableString = (value: unknown) => (typeof value === 'string' ? value : null);
const asBoolean = (value: unknown) => Boolean(value);
const asNumberOrNull = (value: unknown) => (typeof value === 'number' ? value : null);
const asArray = <T = any>(value: unknown): T[] => Array.isArray(value) ? value as T[] : [];

const normalizeObservableBehaviour = (observable: Record<string, any>) => ({
    obId: asString(observable?.obId ?? observable?.ob_id),
    obDescription: asString(observable?.obDescription ?? observable?.ob_description),
    obDashboardName: asString(observable?.obDashboardName ?? observable?.ob_dashboardname),
    obAdaptedDescription: asString(observable?.obAdaptedDescription ?? observable?.ob_adapted_description),
    obDetected: asBoolean(observable?.obDetected ?? observable?.ob_detected),
});

const normalizeObData = (step: Record<string, any>) => ({
    stepId: step?.stepId ?? step?.step_id ?? null,
    stepName: asString(step?.stepName ?? step?.step_name),
    stepDetected: asBoolean(step?.stepDetected ?? step?.step_detected),
    stepDetectionTimestampVideoStart: asString(step?.stepDetectionTimestampVideoStart ?? step?.step_detection_timestamp_video_start),
    stepDetectionTimestampVideoEnd: asString(step?.stepDetectionTimestampVideoEnd ?? step?.step_detection_timestamp_video_end),
    observableBehaviours: asArray<Record<string, any>>(step?.observableBehaviours ?? step?.observable_behaviors).map(normalizeObservableBehaviour),
});

const normalizeGradingValue = (value: Record<string, any>) => ({
    compId: asNumberOrNull(value?.compId ?? value?.comp_id),
    safetyGrade: asNumberOrNull(value?.safetyGrade ?? value?.safety_grade),
    grading: asNumberOrNull(value?.grading ?? value?.grade),
});

const normalizeDetails = (details: Record<string, any> | null | undefined) => {
    if (!details || typeof details !== 'object') {
        return null;
    }

    const procedureSteps = asArray<Record<string, any>>(details?.procedure_steps ?? details?.procedureSteps).map(normalizeObData);
    const obData = asArray<Record<string, any>>(details?.ob_data ?? details?.obData).map(normalizeObData);

    return {
        procedure_steps: procedureSteps,
        ob_data: obData,
        grading_values: asArray<Record<string, any>>(details?.grading_values ?? details?.gradingValues).map(normalizeGradingValue),
    };
};

const normalizeReview = (review: Record<string, any>) => ({
    id: asString(review?.id),
    reviewerId: asString(review?.reviewerId ?? review?.reviewer_id),
    reviewedScore: typeof review?.reviewedScore === 'number' ? review.reviewedScore : 0,
    feedback: asString(review?.feedback),
    timestamp: asString(review?.timestamp),
    details: normalizeDetails(review?.details) ?? {
        procedure_steps: [],
        ob_data: [],
        grading_values: [],
    },
});

const normalizeGradingSnapshot = (grading: Record<string, any>) => ({
    executionId: asString(grading?.executionId ?? grading?.execution_id),
    actorId: asString(grading?.actorId ?? grading?.actor_id),
    feedback: asString(grading?.feedback),
    score: asNumberOrNull(grading?.score),
    reviewedScore: asNumberOrNull(grading?.reviewedScore ?? grading?.reviewed_score),
    isReviewed: grading?.isReviewed ?? grading?.is_reviewed ?? false,
    reviews: asArray<Record<string, any>>(grading?.reviews).map(normalizeReview),
    comments: asArray<string>(grading?.comments),
    details: normalizeDetails(grading?.details),
    isRevoked: grading?.isRevoked ?? grading?.is_revoked ?? false,
    id: asString(grading?.id),
    companyId: asNullableString(grading?.companyId ?? grading?.company_id),
    createdAt: asNullableString(grading?.createdAt ?? grading?.created_at),
    createdBy: asNullableString(grading?.createdBy ?? grading?.created_by),
    updatedAt: asNullableString(grading?.updatedAt ?? grading?.updated_at),
    updatedBy: asNullableString(grading?.updatedBy ?? grading?.updated_by),
});

const ObservableBehaviours = types.model({
    obId: types.optional(types.string, ''),
    obDescription: types.optional(types.string, ''),
    obDashboardName: types.optional(types.string, ''),
    obAdaptedDescription: types.optional(types.string, ''),
    obDetected: types.optional(types.boolean, false),
})

const ObData = types.model({
    stepId: types.maybeNull(types.union(types.string, types.number)),
    stepName: types.optional(types.string, ''),
    stepDetected: types.optional(types.boolean, false),
    stepDetectionTimestampVideoStart: types.optional(types.string, ''),
    stepDetectionTimestampVideoEnd: types.optional(types.string, ''),
    observableBehaviours: types.array(ObservableBehaviours),
})

const GradingValues = types.model({
    compId: types.maybeNull(types.number),
    safetyGrade: types.maybeNull(types.number),
    grading: types.maybeNull(types.number),
})

const Details = types.model({
    procedure_steps: types.array(ObData),
    ob_data: types.array(ObData),
    grading_values: types.array(GradingValues),
})

const Reviews = types.model({
    id: types.string,
    reviewerId: types.string,
    reviewedScore: types.number,
    feedback: types.optional(types.string, ''),
    timestamp: types.string,
    details: Details,
})

export const GradingsInitialState = {
    executionId: '',
    actorId: '',
    feedback: '',
    score: null,
    reviewedScore: null,
    isReviewed: false,
    reviews: [],
    comments: [],
    details: null,
    isRevoked: false,
    id: '',
    companyId: null,
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null,
}

export const GradingsStoreInferred = types
    .model('GradingsStoreInferred', {
        executionId: types.optional(types.string, ''),
        actorId: types.optional(types.string, ''),
        feedback: types.optional(types.string, ''),
        score: types.maybeNull(types.number),
        reviewedScore: types.maybeNull(types.number),
        isReviewed: types.maybeNull(types.boolean),
        reviews: types.optional(types.array(Reviews), []),
        comments: types.optional(types.array(types.string), []),
        details: types.maybeNull(Details),
        isRevoked: types.maybeNull(types.boolean),
        id: types.optional(types.string, ''),
        companyId: types.maybeNull(types.string),
        createdAt: types.maybeNull(types.string),
        createdBy: types.maybeNull(types.string),
        updatedAt: types.maybeNull(types.string),
        updatedBy: types.maybeNull(types.string),

    })
    .views(self => ({
        get getGradingData() {
            return getSnapshot(self);
        },
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        const loadGradings = flow(function* (payload: any) {
            const result = yield performGetGradings(
                api,
                {
                    payload,
                    errorHandlers: { DEFAULT: '' },
                },
            );

            if(result.success){
                const gradings = Array.isArray(result.data) ? result.data : [];

                if (gradings.length > 0) {
                    applySnapshot(self, normalizeGradingSnapshot(gradings[0] ?? {}));
                } else {
                    applySnapshot(self, GradingsInitialState);
                }

                return gradings;
            }

            applySnapshot(self, GradingsInitialState);

            return [];

        });

        const postGrading = flow(function* (payload: any){
            const result = yield postGradings(
                api,
                {
                    payload,
                    errorHandlers: { DEFAULT: '' },
                }
            )

            return result;
        })

        const postReview = flow(function* (payload: any){
            const result = yield addReview(
                api,
                {
                    payload,
                    errorHandlers: { DEFAULT: '' },
                }
            )

            return result;
        })

        const clearData = () =>{
            applySnapshot(self, GradingsInitialState);
        }

        return {
            loadGradings,
            postGrading,
            postReview,
            clearData,
        }
    })

type GradingsStoreFactoryType = typeof GradingsStoreInferred;
interface IGradingsStoreFactory extends GradingsStoreFactoryType { }
export const GradingsStore: IGradingsStoreFactory = GradingsStoreInferred;
export interface IGradingsStore extends Instance<IGradingsStoreFactory> { }
export interface IGradingsStoreSnapshotIn extends SnapshotIn<IGradingsStore> { }
